import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';

import {
  ArrowLeftIcon,
  Cross2Icon,
  CubeIcon,
  ExitIcon,
  HamburgerMenuIcon,
  HomeIcon,
  MagicWandIcon,
} from '@radix-ui/react-icons';
import clsx from 'clsx';
import { AnimatePresence, motion } from 'framer-motion';

import { useAuthStore } from '@/features/auth/model/auth-store';

import { ROUTES } from '@/shared/config/routes';

interface MenuItem {
  label: string;
  href: string;
  icon: React.ReactNode;
}

export const MobileNav = () => {
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, isAuthenticated } = useAuthStore();

  const menuItems: MenuItem[] = [
    {
      label: 'Home',
      href: ROUTES.HOME,
      icon: <HomeIcon className="w-5 h-5" />,
    },
    {
      label: 'Model',
      href: ROUTES.MODEL,
      icon: <CubeIcon className="w-5 h-5" />,
    },
    {
      label: 'Look Like',
      href: ROUTES.LOOK_LIKE,
      icon: <MagicWandIcon className="w-5 h-5" />,
    },
  ];
  const visibleItems = menuItems.slice(0, 3); // Show 3 items in bottom nav
  // const hiddenItems = menuItems.slice(3);

  const NavItem = ({ item }: { item: MenuItem }) => {
    const isActive = location.pathname === item.href;

    return (
      <Link
        to={item.href}
        className={clsx(
          'flex flex-col items-center justify-center py-2 px-3 rounded-lg transition-all',
          isActive ? 'text-violet-500' : 'text-zinc-400',
        )}
        onClick={() => setIsMoreMenuOpen(false)}
      >
        {item.icon}
        <span className="text-xs mt-1">{item.label}</span>
      </Link>
    );
  };

  return (
    <>
      {/* Bottom Navigation Bar */}
      <nav className="mobile-nav fixed bottom-0 left-0 right-0 bg-zinc-800 border-t border-zinc-700 px-4 lg:hidden">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-2.5 flex-1">
            {visibleItems.map((item) => (
              <NavItem key={item.href} item={item} />
            ))}
          </div>

          {/* More Button */}
          <button
            onClick={() => setIsMoreMenuOpen(true)}
            className="flex flex-col items-center justify-center py-2 px-3 rounded-lg text-zinc-400 cursor-pointer"
          >
            <HamburgerMenuIcon className="w-5 h-5" />
            <span className="text-xs mt-1">More</span>
          </button>
        </div>
      </nav>

      {/* Full-screen More Menu */}
      <AnimatePresence>
        {isMoreMenuOpen && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/50 z-40 lg:hidden"
              onClick={() => setIsMoreMenuOpen(false)}
            />

            <motion.div
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 300 }}
              className="fixed inset-y-0 left-0 w-full max-w-sm bg-zinc-800 z-50 overflow-y-auto lg:hidden"
            >
              <div className="p-4">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-xl font-semibold text-white">Menu</h2>
                  <button
                    onClick={() => setIsMoreMenuOpen(false)}
                    className="p-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-700 transition-colors cursor-pointer"
                  >
                    <Cross2Icon className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-2">
                  {/* Navigation Items */}
                  {menuItems.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.href}
                        to={item.href}
                        onClick={() => setIsMoreMenuOpen(false)}
                        className={clsx(
                          'flex items-center gap-3 px-4 py-3 rounded-xl transition-all',
                          'hover:bg-zinc-700',
                          isActive && 'bg-violet-600 text-white',
                          !isActive && 'text-zinc-300 hover:text-white',
                        )}
                      >
                        {item.icon}
                        <span className="font-medium">{item.label}</span>
                      </Link>
                    );
                  })}

                  {/* Divider */}
                  <div className="my-4 border-t border-zinc-700" />

                  {/* Login/Logout Button */}
                  {isAuthenticated ? (
                    <button
                      onClick={() => {
                        setIsMoreMenuOpen(false);
                        logout();
                        navigate(ROUTES.HOME);
                        window.location.reload();
                      }}
                      className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors font-medium cursor-pointer"
                    >
                      <ArrowLeftIcon className="w-4 h-4" />
                      <span>Logout</span>
                    </button>
                  ) : (
                    <Link
                      to={ROUTES.AUTH.REGISTER}
                      onClick={() => setIsMoreMenuOpen(false)}
                      className="flex items-center gap-3 px-4 py-3 rounded-xl transition-all hover:bg-zinc-700 text-zinc-300 hover:text-white"
                    >
                      <ExitIcon className="w-5 h-5" />
                      <span className="font-medium">Sign In</span>
                    </Link>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};
